import React, { useState } from "react";
import { Search, CalendarCheck, Star, Clock, MapPin } from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   FILE: src/SUPERAPP/BOOKING/APPOINTMENT/AppointmentHome.tsx
   Facebook-style theme: blue / white / black. Self-contained —
   swap MOCK_PROVIDERS for a real fetch/query when ready.
═══════════════════════════════════════════════════════════ */

const FB = {
  bg: "#F0F2F5",
  panelBg: "#FFFFFF",
  black: "#050505",
  textMuted: "#65676B",
  blue: "#1877F2",
  blueSoft: "#E7F3FF",
  border: "#DADDE1",
};

interface Provider {
  id: number;
  name: string;
  specialty: string;
  location: string;
  nextSlot: string;
  rating: number;
}

const MOCK_PROVIDERS: Provider[] = [
  { id: 1, name: "Dr. Elena Ross", specialty: "Dentist", location: "Downtown Clinic", nextSlot: "Today 3:30 PM", rating: 4.8 },
  { id: 2, name: "Marcus Lee, LMT", specialty: "Massage Therapist", location: "Wellness Studio", nextSlot: "Tomorrow 10:00 AM", rating: 4.9 },
  { id: 3, name: "Dr. Priya Nair", specialty: "Dermatologist", location: "Skin & Care Center", nextSlot: "Jul 9, 1:00 PM", rating: 4.7 },
  { id: 4, name: "Sam Torres", specialty: "Hair Stylist", location: "Studio 12 Salon", nextSlot: "Today 5:00 PM", rating: 4.6 },
  { id: 5, name: "Dr. Alan Kim", specialty: "Optometrist", location: "ClearView Eyecare", nextSlot: "Jul 10, 9:30 AM", rating: 4.8 },
  { id: 6, name: "Nina Patel, RD", specialty: "Nutritionist", location: "Balanced Health", nextSlot: "Jul 8, 2:15 PM", rating: 4.9 },
];

const AppointmentHome: React.FC = () => {
  const [query, setQuery] = useState("");

  const filtered = MOCK_PROVIDERS.filter(
    (p) =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.specialty.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div style={S.wrap}>
      <div style={S.searchBar}>
        <Search size={16} color={FB.textMuted} />
        <input
          style={S.searchInput}
          placeholder="Search providers or specialties"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div style={S.grid}>
        {filtered.map((p) => (
          <div key={p.id} style={S.card}>
            <div style={S.thumb}>
              <CalendarCheck size={24} color={FB.blue} />
            </div>
            <div style={S.cardBody}>
              <div style={S.cardSpecialty}>{p.specialty}</div>
              <div style={S.cardName}>{p.name}</div>
              <div style={S.cardLocation}>
                <MapPin size={11} /> {p.location}
              </div>
              <div style={S.cardBottomRow}>
                <span style={S.cardSlot}>
                  <Clock size={12} /> {p.nextSlot}
                </span>
                <span style={S.cardRating}>
                  <Star size={12} color="#F5A623" fill="#F5A623" /> {p.rating}
                </span>
              </div>
              <button style={S.addBtn}>Book appointment</button>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={S.empty}>No providers match "{query}".</div>
        )}
      </div>
    </div>
  );
};

export default AppointmentHome;

const S: Record<string, React.CSSProperties> = {
  wrap: { padding: "16px 20px 40px", maxWidth: 980, margin: "0 auto" },
  searchBar: {
    display: "flex", alignItems: "center", gap: 8, background: FB.panelBg,
    border: `1px solid ${FB.border}`, borderRadius: 20, padding: "8px 14px", marginBottom: 18,
  },
  searchInput: { border: "none", outline: "none", flex: 1, fontSize: 14, background: "transparent", color: FB.black },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 14 },
  card: { background: FB.panelBg, border: `1px solid ${FB.border}`, borderRadius: 10, overflow: "hidden" },
  thumb: { height: 90, background: FB.blueSoft, display: "flex", alignItems: "center", justifyContent: "center" },
  cardBody: { padding: "10px 12px 12px" },
  cardSpecialty: { fontSize: 11, color: FB.textMuted, fontWeight: 600, textTransform: "uppercase" },
  cardName: { fontSize: 14, fontWeight: 700, color: FB.black, margin: "2px 0 6px" },
  cardLocation: { fontSize: 12, color: FB.textMuted, display: "flex", alignItems: "center", gap: 4, marginBottom: 8 },
  cardBottomRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  cardSlot: { fontSize: 12, color: FB.black, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 },
  cardRating: { fontSize: 12, color: FB.textMuted, display: "flex", alignItems: "center", gap: 3 },
  addBtn: {
    width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    background: FB.blue, color: "#fff", border: "none", borderRadius: 6, padding: "8px 0",
    fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  empty: { gridColumn: "1 / -1", textAlign: "center", color: FB.textMuted, fontSize: 13.5, padding: "40px 0" },
};
